import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert, IconButton, Divider, Button } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningIcon from '@mui/icons-material/Warning';
import RecommendIcon from '@mui/icons-material/Recommend';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BlockIcon from '@mui/icons-material/Block';
import { formatDateForFirebase } from '../utils/dateUtils';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { format, addDays, subDays, subMonths, startOfYear, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTheme } from '@mui/material/styles';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ScatterChart, Scatter, PieChart, Pie, Cell, BarChart as ReBarChart, Bar as ReBar, ResponsiveContainer as ReResponsiveContainer, Tooltip as ReTooltip, Legend as ReLegend, XAxis as ReXAxis, YAxis as ReYAxis, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { TextField, FormGroup, FormControlLabel, Checkbox, Switch, Select, MenuItem, InputLabel, FormControl, OutlinedInput, Chip } from '@mui/material';
import { isSameDay, toLocalTime, convertJSDayToSystemDay } from '../utils/dateUtils';
import { usePlan } from '../contexts/PlanContext';
import PlanComparisonDialog from './shared/PlanComparisonDialog';

function StatCard({ icon, title, value, color = 'primary', customColors }) {
  return (
    <Card sx={{ 
      p: 2, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      borderRadius: 2,
      boxShadow: 2,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4
      }
    }}>
      <Box sx={{ 
        color: customColors?.primary || color, 
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {React.cloneElement(icon, { 
          sx: { fontSize: { xs: 32, sm: 40 } }
        })}
      </Box>
      <Typography 
        variant="h6" 
        component="div" 
        gutterBottom 
        sx={{ 
          fontSize: { xs: '0.875rem', sm: '1rem' },
          fontWeight: 'bold',
          lineHeight: 1.2,
          mb: 1,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="h6" 
        sx={{ 
          color: customColors?.primary || 'primary.main',
          fontSize: { xs: '1rem', sm: '1.25rem' },
          fontWeight: 'bold'
        }}
      >
        {value}
      </Typography>
    </Card>
  );
}

function Statistics({ habits = [], goals = [], challenges = [], customColors }) {
  const { currentUser } = useAuth();
  const [monthlyStatsExpanded, setMonthlyStatsExpanded] = useState(false);
  const [dailyStatsExpanded, setDailyStatsExpanded] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [periodStart, setPeriodStart] = useState(() => {
    // Padrão: 30 dias atrás
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState(() => {
    // Hoje
    return new Date().toISOString().split('T')[0];
  });
  const [selectedHabits, setSelectedHabits] = useState(['todos']);
  const [highlightPerfectDays, setHighlightPerfectDays] = useState(true);
  const [activePreset, setActivePreset] = useState(null);
  const { permissions } = usePlan();
  const [openPlanDialog, setOpenPlanDialog] = useState(false);

  const DEFAULT_PILLAR_COLORS = {
    vitalidade: '#2E7D32',
    mentalidade: '#F9A825',
    prosperidade: '#E65100'
  };

  const PILLAR_LABELS = {
    vitalidade: 'Vitalidade',
    mentalidade: 'Mentalidade',
    prosperidade: 'Prosperidade'
  };

  // Lista de hábitos ativos
  const habitOptions = habits.filter(h => h.active !== false);

  // Configuração para o Select do Material UI não fechar ao clicar
  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 320,
      },
    },
    disableAutoFocusItem: true,
    MenuListProps: {
      disablePadding: true,
    },
    autoFocus: false,
  };

  // Handler para seleção de hábitos
  const handleHabitDropdown = (event) => {
    let value = event.target.value;
    if (!Array.isArray(value)) value = [value];
    const allIds = habitOptions.map(h => h.id);

    // Se clicar em 'Todos', seleciona só ele
    if (value.includes('todos') && value.length === 1) {
      setSelectedHabits(['todos']);
      return;
    }

    // Se 'todos' está junto com outros, remova 'todos'
    if (value.includes('todos') && value.length > 1) {
      value = value.filter(v => v !== 'todos');
    }

    // Se todos os hábitos forem selecionados manualmente, marca 'Todos'
    if (value.length === allIds.length) {
      setSelectedHabits(['todos']);
      return;
    }

    // Se desmarcar tudo, volta para 'Todos'
    if (value.length === 0) {
      setSelectedHabits(['todos']);
      return;
    }

    setSelectedHabits(value);
  };

  // Gera array de datas do período
  const getDatesInRange = (start, end) => {
    const arr = [];
    let dt = new Date(start);
    const endDt = new Date(end);
    while (dt <= endDt) {
      arr.push(dt.toISOString().split('T')[0]);
      dt.setDate(dt.getDate() + 1);
    }
    return arr;
  };

  // Função auxiliar para verificar se um hábito está completo em uma data (igual Calendário)
  function isHabitCompletedOnDate(habit, dateStr) {
    if (!habit?.completedDates) return false;
    return habit.completedDates.some(completedDate => isSameDay(completedDate, dateStr));
  }

  // Função auxiliar para verificar se um hábito está ativo em um dia da semana (igual Calendário)
  function isHabitActiveOnDay(habit, dateStr) {
    if (!habit || !habit.active) return false;
    const date = toLocalTime(dateStr);
    if (!date) return false;

    // Se o hábito tem data de início, ignora dias anteriores a essa data
    if (habit.startDate) {
      const parts = habit.startDate.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        if (year && month && day) {
          const start = new Date(year, month - 1, day);
          start.setHours(0, 0, 0, 0);
          if (date < start) return false;
        }
      }
    }

    const jsDay = date.getDay();
    const systemDay = convertJSDayToSystemDay(jsDay);
    if (!habit.weekdays || habit.weekdays.length === 0) {
      return true;
    }
    return habit.weekdays.includes(systemDay);
  }

  // Função para verificar se o dia é perfeito (igual Calendário)
  function isDayComplete(habits, dateStr) {
    if (!habits || !Array.isArray(habits)) return false;
    const activeHabits = habits.filter(habit =>
      habit &&
      habit.active !== false &&
      isHabitActiveOnDay(habit, dateStr)
    );
    if (activeHabits.length === 0) return false;
    return activeHabits.every(habit => isHabitCompletedOnDate(habit, dateStr));
  }

  // Função para formatar datas para exibição no eixo X (dd/MM)
  function formatXAxisDate(dateStr) {
    // dateStr pode ser '05-20' ou '2025-05-20'
    if (dateStr.length === 5 && dateStr.includes('-')) {
      // MM-DD
      const [mm, dd] = dateStr.split('-');
      return `${dd}/${mm}`;
    }
    if (dateStr.length === 10 && dateStr.includes('-')) {
      // yyyy-MM-dd
      const [yyyy, mm, dd] = dateStr.split('-');
      return `${dd}/${mm}`;
    }
    return dateStr;
  }

  // Função para formatar datas para tooltip (dd/MM/yy)
  function formatTooltipDate(dateStr) {
    // dateStr pode ser '05-20' ou '2025-05-20'
    if (dateStr.length === 5 && dateStr.includes('-')) {
      // MM-DD
      const [mm, dd] = dateStr.split('-');
      const year = periodEnd.slice(2, 4); // Pega os dois últimos dígitos do ano final do período
      return `${dd}/${mm}/${year}`;
    }
    if (dateStr.length === 10 && dateStr.includes('-')) {
      // yyyy-MM-dd
      const [yyyy, mm, dd] = dateStr.split('-');
      return `${dd}/${mm}/${yyyy.slice(2)}`;
    }
    return dateStr;
  }

  // Função para buscar o ano correto a partir do payload, considerando diferentes formatos
  function getYearFromPayload(payload) {
    if (!payload || !payload.length) return new Date().getFullYear();
    // Tenta pegar o ano do primeiro dado, se disponível
    if (payload[0].payload) {
      // Tenta pegar do campo 'date' (usado nos gráficos)
      const dateStr = payload[0].payload.date;
      if (dateStr) {
        // yyyy-MM-dd
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return dateStr.split('-')[0];
        }
        // MM-DD
        if (/^\d{2}-\d{2}$/.test(dateStr)) {
          // Tenta pegar do campo 'fullDate' se existir
          if (payload[0].payload.fullDate && /^\d{4}-\d{2}-\d{2}$/.test(payload[0].payload.fullDate)) {
            return payload[0].payload.fullDate.split('-')[0];
          }
        }
      }
      // Tenta pegar do campo 'fullDate' se existir
      if (payload[0].payload.fullDate && /^\d{4}-\d{2}-\d{2}$/.test(payload[0].payload.fullDate)) {
        return payload[0].payload.fullDate.split('-')[0];
      }
    }
    // Fallback: tenta pegar do label se for yyyy-MM-dd
    if (payload[0].payload && payload[0].payload.label && /^\d{4}-\d{2}-\d{2}$/.test(payload[0].payload.label)) {
      return payload[0].payload.label.split('-')[0];
    }
    // Fallback: ano atual
    return new Date().getFullYear();
  }

  // Função para formatar label da tooltip como dd/mm/aaaa
  function formatTooltipLabelToFullDate(label, payload) {
    if (!label) return '';
    // label pode ser '2024-06-10', '06-10', etc
    if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
      const [yyyy, mm, dd] = label.split('-');
      return `${dd}/${mm}/${yyyy}`;
    }
    if (/^\d{2}-\d{2}$/.test(label)) {
      // Se não tiver ano, tenta pegar do payload
      const [mm, dd] = label.split('-');
      const yyyy = getYearFromPayload(payload);
      return `${dd}/${mm}/${yyyy}`;
    }
    return label;
  }

  // Função para converter '04/2025' ou '04/25' para 'Abril/25'
  function formatMonthLabel(monthLabel) {
    // monthLabel: '04/2025' ou '04/25'
    let [mm, yy] = monthLabel.split('/');
    if (yy.length === 4) yy = yy.slice(2); // pega só os dois últimos dígitos
    const monthNames = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    const monthIdx = parseInt(mm, 10) - 1;
    return `${monthNames[monthIdx]}/${yy}`;
  }

  // Lista de hábitos filtrados pelo dropdown
  const filteredHabitOptions = selectedHabits.includes('todos') ? habitOptions : habitOptions.filter(h => selectedHabits.includes(h.id));

  // Gráfico de Vida: equilíbrio entre pilares
  const pillarRadarData = React.useMemo(() => {
    if (!habits.length) {
      return Object.entries(PILLAR_LABELS).map(([id, name]) => ({
        id,
        name,
        score: 0,
        scheduled: 0,
        completed: 0,
        color: customColors?.pillars?.[id] || DEFAULT_PILLAR_COLORS[id]
      }));
    }

    const statsByPillar = {};
    const dates = getDatesInRange(periodStart, periodEnd);
    const selectedIds = selectedHabits.includes('todos')
      ? habitOptions.map(h => h.id)
      : selectedHabits;

    dates.forEach(dateStr => {
      const jsDate = new Date(dateStr);
      habitOptions.forEach(habit => {
        if (!selectedIds.includes(habit.id)) return;

        // Respeita a data de início do hábito
        if (habit.startDate) {
          const parts = habit.startDate.split('-');
          if (parts.length === 3) {
            const [y, m, d] = parts.map(Number);
            if (y && m && d) {
              const start = new Date(y, m - 1, d);
              start.setHours(0, 0, 0, 0);
              if (jsDate < start) return;
            }
          }
        }

        const dayOfWeek = jsDate.getDay();
        const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;

        if (!habit.weekdays?.length || habit.weekdays.includes(adjustedDay)) {
          const pillarId = (habit.pillar === 'expansao' ? 'mentalidade' : (habit.pillar || 'vitalidade'));
          if (!statsByPillar[pillarId]) {
            statsByPillar[pillarId] = { scheduled: 0, completed: 0 };
          }
          statsByPillar[pillarId].scheduled++;
          if (habit.completedDates?.some(completedDate => completedDate.split('T')[0] === dateStr)) {
            statsByPillar[pillarId].completed++;
          }
        }
      });
    });

    return Object.entries(PILLAR_LABELS).map(([id, name]) => {
      const stats = statsByPillar[id] || { scheduled: 0, completed: 0 };
      const score = stats.scheduled > 0
        ? Math.round((stats.completed / stats.scheduled) * 100)
        : 0;
      return {
        id,
        name,
        score,
        scheduled: stats.scheduled,
        completed: stats.completed,
        color: customColors?.pillars?.[id] || DEFAULT_PILLAR_COLORS[id]
      };
    });
  }, [habits, habitOptions, selectedHabits, periodStart, periodEnd, customColors, PILLAR_LABELS, DEFAULT_PILLAR_COLORS]);

  // Gráfico 2: Realizados vs Programados (com destaque para dias perfeitos)
  const realizedVsPlannedData = React.useMemo(() => {
    const dates = getDatesInRange(periodStart, periodEnd);
    const selected = selectedHabits.includes('todos') ? habitOptions.map(h => h.id) : selectedHabits;
    return dates.map(dateStr => {
      let realizados = 0;
      let programados = 0;
      habitOptions.forEach(habit => {
        if (!selected.includes(habit.id)) return;
        const jsDate = new Date(dateStr);
        // Respeita a data de início do hábito
        if (habit.startDate) {
          const parts = habit.startDate.split('-');
          if (parts.length === 3) {
            const [year, month, day] = parts.map(Number);
            if (year && month && day) {
              const start = new Date(year, month - 1, day);
              start.setHours(0, 0, 0, 0);
              if (jsDate < start) return;
            }
          }
        }
        const dayOfWeek = jsDate.getDay();
        const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
        if (!habit.weekdays?.length || habit.weekdays.includes(adjustedDay)) {
          programados++;
          if (habit.completedDates?.some(completedDate => completedDate.split('T')[0] === dateStr)) {
            realizados++;
          }
        }
      });
      // Adiciona flag de dia perfeito
      const perfeito = isDayComplete(filteredHabitOptions, dateStr);
      return {
        date: dateStr.slice(5), // MM-DD
        'Realizados': realizados,
        perfeito
      };
    });
  }, [periodStart, periodEnd, habits, selectedHabits]);

  // Gráfico Dotplot: Eixo X = dia, Eixo Y = mês
  const dotplotMonthData = React.useMemo(() => {
    // Gera lista de meses únicos no período
    const dates = getDatesInRange(periodStart, periodEnd);
    const monthsSet = new Set();
    dates.forEach(dateStr => {
      const [yyyy, mm] = dateStr.split('-');
      monthsSet.add(`${mm}/${yyyy.slice(2)}`);
    });
    const monthsArr = Array.from(monthsSet).sort((a, b) => {
      // Ordena por ano e mês
      const [ma, ya] = a.split('/').map(Number);
      const [mb, yb] = b.split('/').map(Number);
      return ya !== yb ? ya - yb : ma - mb;
    });
    // Para cada mês, para cada dia, gera ponto
    let dots = [];
    monthsArr.forEach(monthLabel => {
      const [mm, yy] = monthLabel.split('/');
      const yyyy = '20' + yy.padStart(2, '0');
      const daysInMonth = new Date(yyyy, mm, 0).getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const dayStr = String(day).padStart(2, '0');
        const dateStr = `${yyyy}-${mm}-${dayStr}`;
        // Só considera se está dentro do período
        if (dateStr < periodStart || dateStr > periodEnd) continue;
        // Quais hábitos programados para o dia
        const activeHabits = filteredHabitOptions.filter(habit => isHabitActiveOnDay(habit, dateStr));
        if (activeHabits.length === 0) continue;
        const realizados = activeHabits.filter(habit => isHabitCompletedOnDate(habit, dateStr)).length;
        let color = '#e53935'; // vermelho
        if (realizados === activeHabits.length) color = '#43a047'; // verde
        else if (realizados > 0) color = '#fbc02d'; // amarelo
        dots.push({
          x: day,
          y: monthLabel,
          color,
          perfeito: color === '#43a047',
          parcial: color === '#fbc02d',
          nenhum: color === '#e53935',
          date: dateStr
        });
      }
    });
    return dots;
  }, [periodStart, periodEnd, habits, selectedHabits]);

  // Novo cálculo de estatísticas filtradas pelo período selecionado
  const filteredStats = React.useMemo(() => {
    // Filtra hábitos concluídos e dias perfeitos apenas dentro do período e dos hábitos selecionados
    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    let minYear = start.getFullYear();
    let maxYear = end.getFullYear();
    let monthlyStats = [];
    let totalPerfectDays = 0;
    let totalCompletions = 0;
    let bestMonth = null;
    let worstMonth = null;
    for (let year = minYear; year <= maxYear; year++) {
      for (let month = 0; month < 12; month++) {
        const monthStart = new Date(year, month, 1);
        const monthEnd = new Date(year, month + 1, 0);
        // Só processa meses dentro do período
        if (monthEnd < start || monthStart > end) continue;
        let monthCompletions = 0;
        let monthScheduled = 0;
        let monthPerfectDays = 0;
        for (let date = new Date(monthStart); date <= monthEnd; date.setDate(date.getDate() + 1)) {
          if (date < start || date > end) continue;
          const dateStr = date.toISOString().split('T')[0];
          const formattedDate = formatDateForFirebase(date);
          const activeHabits = filteredHabitOptions.filter(habit => {
            if (!habit.active) return false;
            // Respeita a data de início do hábito
            if (habit.startDate) {
              const parts = habit.startDate.split('-');
              if (parts.length === 3) {
                const [y, m, d] = parts.map(Number);
                if (y && m && d) {
                  const startDate = new Date(y, m - 1, d);
                  startDate.setHours(0, 0, 0, 0);
                  if (date < startDate) return false;
                }
              }
            }
            const dayOfWeek = date.getDay();
            const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
            return !habit.weekdays?.length || habit.weekdays.includes(adjustedDay);
          });
          const completedHabits = activeHabits.filter(habit => 
            habit.completedDates?.some(completedDate => {
              const completedDateOnly = completedDate.split('T')[0];
              const formattedDateOnly = formattedDate.split('T')[0];
              return completedDateOnly === formattedDateOnly;
            })
          );
          const isPerfectDay = activeHabits.length > 0 && 
            activeHabits.every(habit => 
              habit.completedDates?.some(completedDate => {
                const completedDateOnly = completedDate.split('T')[0];
                const formattedDateOnly = formattedDate.split('T')[0];
                return completedDateOnly === formattedDateOnly;
              })
            );
          monthCompletions += completedHabits.length;
          monthScheduled += activeHabits.length;
          if (isPerfectDay) {
            monthPerfectDays++;
            totalPerfectDays++;
          }
        }
        totalCompletions += monthCompletions;
        const utilizationRate = monthScheduled > 0 
          ? Math.round((monthCompletions / monthScheduled) * 100) 
          : 0;
        const monthData = {
          name: format(new Date(year, month), 'MMMM', { locale: ptBR }),
          month: month,
          year: year,
          completedHabits: monthCompletions,
          scheduledHabits: monthScheduled,
          utilizationRate,
          perfectDays: monthPerfectDays
        };
        monthlyStats.push(monthData);
        if (
          monthScheduled > 0 &&
          (!bestMonth || utilizationRate > bestMonth.rate)
        ) {
          bestMonth = {
            name: monthData.name,
            year: monthData.year,
            rate: utilizationRate
          };
        }
        if (
          monthScheduled > 0 &&
          (!worstMonth || utilizationRate < worstMonth.rate)
        ) {
          worstMonth = {
            name: monthData.name,
            year: monthData.year,
            rate: utilizationRate
          };
        }
      }
    }
    monthlyStats.sort((a, b) => (a.year - b.year) || (a.month - b.month));
    return {
      months: monthlyStats,
      totalPerfectDays,
      totalCompletions,
      bestMonth,
      worstMonth
    };
  }, [habits, periodStart, periodEnd, selectedHabits]);

  const calculateProgress = (goal) => {
    if (goal.completed) return 100;
    if (!goal.subtasks.length) return 0;
    const completedSubtasks = goal.subtasks.filter(subtask => subtask.completed).length;
    return Math.round((completedSubtasks / goal.subtasks.length) * 100);
  };

  const generateRecommendations = (stats) => {
    const recommendations = [];

    // Recomendação baseada no total de dias perfeitos
    if (stats.totalPerfectDays === 0) {
      recommendations.push({
        icon: <EmojiEventsIcon />,
        message: "Tente completar todos os hábitos em um dia para alcançar seu primeiro dia perfeito!"
      });
    } else if (stats.totalPerfectDays > 0) {
      recommendations.push({
        icon: <EmojiEventsIcon />,
        message: `Parabéns! Você já teve ${stats.totalPerfectDays} ${stats.totalPerfectDays === 1 ? 'dia perfeito' : 'dias perfeitos'}. Continue assim!`
      });
    }

    // Recomendação baseada no melhor mês
    if (stats.bestMonth?.rate > 0) {
      recommendations.push({
        icon: <TrendingUpIcon />,
        message: `Seu melhor progresso foi em ${stats.bestMonth.name}/${stats.bestMonth.year} com ${stats.bestMonth.rate}% de conclusão.`
      });
    }

    // Recomendação baseada no pior mês
    if (stats.worstMonth?.rate < 50) {
      recommendations.push({
        icon: <TrendingDownIcon />,
        message: `Tente melhorar seu progresso em relação a ${stats.worstMonth.name}/${stats.worstMonth.year}, onde teve ${stats.worstMonth.rate}% de conclusão.`
      });
    }

    // Recomendação baseada no total de hábitos concluídos
    if (stats.totalCompletions > 0) {
      recommendations.push({
        icon: <CheckCircleIcon />,
        message: `Você já completou ${stats.totalCompletions} ${stats.totalCompletions === 1 ? 'hábito' : 'hábitos'}! Continue mantendo a consistência.`
      });
    }

    return recommendations;
  };

  const recommendations = generateRecommendations(filteredStats);

  // Atualiza o valor de dias perfeitos no documento do usuário
  useEffect(() => {
    if (currentUser && filteredStats.totalPerfectDays !== undefined) {
      const userRef = doc(db, 'users', currentUser.uid);
      updateDoc(userRef, {
        totalPerfectDays: filteredStats.totalPerfectDays
      });
    }
  }, [currentUser, filteredStats.totalPerfectDays]);

  // Função para comparar datas (yyyy-MM-dd)
  function isSameDate(a, b) {
    return a && b && a.slice(0, 10) === b.slice(0, 10);
  }

  // Função para checar se o período atual bate com algum preset
  useEffect(() => {
    const end = new Date();
    const presets = [
      {
        key: '7d',
        start: (() => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().split('T')[0]; })(),
        end: end.toISOString().split('T')[0]
      },
      {
        key: '15d',
        start: (() => { const d = new Date(); d.setDate(d.getDate() - 14); return d.toISOString().split('T')[0]; })(),
        end: end.toISOString().split('T')[0]
      },
      {
        key: '30d',
        start: (() => { const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().split('T')[0]; })(),
        end: end.toISOString().split('T')[0]
      },
      {
        key: '3m',
        start: (() => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().split('T')[0]; })(),
        end: end.toISOString().split('T')[0]
      },
      {
        key: '6m',
        start: (() => { const d = new Date(); d.setMonth(d.getMonth() - 6); return d.toISOString().split('T')[0]; })(),
        end: end.toISOString().split('T')[0]
      },
      {
        key: '12m',
        start: (() => { const d = new Date(); d.setMonth(d.getMonth() - 12); return d.toISOString().split('T')[0]; })(),
        end: end.toISOString().split('T')[0]
      },
      {
        key: 'ytd',
        start: (() => { const d = new Date(); d.setMonth(0, 1); return d.toISOString().split('T')[0]; })(),
        end: end.toISOString().split('T')[0]
      }
    ];
    const found = presets.find(p => isSameDate(periodStart, p.start) && isSameDate(periodEnd, p.end));
    setActivePreset(found ? found.key : null);
  }, [periodStart, periodEnd]);

  // Debug: mostrar o valor de selectedHabits a cada render
  console.log('selectedHabits:', selectedHabits);

  // Presets de período para dropdown mobile
  const periodPresets = [
    { key: '7d', label: 'Últimos 7 dias', getRange: () => {
      const end = new Date();
      const start = new Date(); start.setDate(end.getDate() - 6);
      return [start, end];
    }},
    { key: '15d', label: 'Últimos 15 dias', getRange: () => {
      const end = new Date();
      const start = new Date(); start.setDate(end.getDate() - 14);
      return [start, end];
    }},
    { key: '30d', label: 'Últimos 30 dias', getRange: () => {
      const end = new Date();
      const start = new Date(); start.setDate(end.getDate() - 29);
      return [start, end];
    }},
    { key: '3m', label: 'Último Trimestre', getRange: () => {
      const end = new Date();
      const start = new Date(); start.setMonth(end.getMonth() - 3);
      return [start, end];
    }},
    { key: '6m', label: 'Último Semestre', getRange: () => {
      const end = new Date();
      const start = new Date(); start.setMonth(end.getMonth() - 6);
      return [start, end];
    }},
    { key: '12m', label: 'Último Ano', getRange: () => {
      const end = new Date();
      const start = new Date(); start.setMonth(end.getMonth() - 12);
      return [start, end];
    }},
    { key: 'ytd', label: 'Ano até aqui', getRange: () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), 0, 1);
      return [start, end];
    }},
  ];
  const [mobilePreset, setMobilePreset] = React.useState('');

  // Função para interpolar entre dois verdes conforme a correlação
  function interpolateGreen(corr) {
    // De #A5D6A7 (verde claro) para #388E3C (verde escuro)
    const from = [165, 214, 167];
    const to = [56, 142, 60];
    const r = Math.round(from[0] + (to[0] - from[0]) * corr);
    const g = Math.round(from[1] + (to[1] - from[1]) * corr);
    const b = Math.round(from[2] + (to[2] - from[2]) * corr);
    return `rgb(${r},${g},${b})`;
  }

  // Função para decidir cor do texto baseada no fundo
  function getContrastingText(bgColor) {
    let rgb;
    if (bgColor.startsWith('hsl')) {
      // Extrai valores H, S, L
      const [h, s, l] = bgColor.match(/\d+/g).map(Number);
      // Conversão HSL para RGB
      const sNorm = s / 100;
      const lNorm = l / 100;
      const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
      const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
      const m = lNorm - c / 2;
      let r1 = 0, g1 = 0, b1 = 0;
      if (h >= 0 && h < 60) { r1 = c; g1 = x; b1 = 0; }
      else if (h >= 60 && h < 120) { r1 = x; g1 = c; b1 = 0; }
      else if (h >= 120 && h < 180) { r1 = 0; g1 = c; b1 = x; }
      else if (h >= 180 && h < 240) { r1 = 0; g1 = x; b1 = c; }
      else if (h >= 240 && h < 300) { r1 = x; g1 = 0; b1 = c; }
      else if (h >= 300 && h < 360) { r1 = c; g1 = 0; b1 = x; }
      const r = Math.round((r1 + m) * 255);
      const g = Math.round((g1 + m) * 255);
      const b = Math.round((b1 + m) * 255);
      rgb = [r, g, b];
    } else {
      rgb = bgColor.match(/\d+/g).map(Number);
    }
    // Luminosidade relativa (fórmula WCAG)
    const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
    return luminance < 0.5 ? '#fff' : '#222';
  }

  // Função para converter hex para HSL
  function hexToHSL(hex) {
    // Remove o # se existir
    hex = hex.replace('#', '');
    // Converte para RGB
    let r = 0, g = 0, b = 0;
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  }

  // Função para interpolar luminosidade da cor principal
  function interpolateMainColor(corr) {
    // Pega a cor principal do app ou fallback
    let base = customColors?.primary || '#1976d2';
    // Converte para HSL
    const hsl = hexToHSL(base);
    // Luminância: de 85% (claro) a 35% (escuro)
    const l = Math.round(85 - (corr * 50));
    return `hsl(${hsl.h}, ${hsl.s}%, ${l}%)`;
  }

  // Renderizar apenas o Gráfico 02 - Realizados e os cards se permissions.canSeeOnlyBasicStatistics for true
  // Esconder os demais gráficos e seções para o plano Básico
  if (permissions && permissions.canSeeOnlyBasicStatistics) {
    // Renderização exclusiva para o plano Básico
    return (
      <Box sx={{ p: 0, pt: { xs: 0, sm: 2 } }}>
        {/* Filtros de período e seleção de hábitos, com presets de intervalo */}
        <Box sx={{ mb: { xs: 1.5, sm: 5 } }}>
          <Box sx={{ display: { xs: 'block', sm: 'flex' }, alignItems: 'center', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' }, mb: 2 }}>
            <TextField
              label="Data Inicial"
              type="date"
              size="small"
              value={periodStart}
              onChange={e => setPeriodStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160, flexShrink: 0, mr: 1, mb: { xs: 1, sm: 0 } }}
              InputProps={{
                sx: {
                  '& input[type="date"]::-webkit-calendar-picker-indicator': {
                    backgroundColor: customColors?.primary || 'primary.main',
                    backgroundImage: 'none',
                    maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center',
                    maskSize: 'contain',
                    WebkitMaskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    WebkitMaskSize: 'contain',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }
                }
              }}
            />
            <TextField
              label="Data Final"
              type="date"
              size="small"
              value={periodEnd}
              onChange={e => setPeriodEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160, flexShrink: 0, mr: 2, mb: { xs: 1, sm: 0 } }}
              InputProps={{
                sx: {
                  '& input[type="date"]::-webkit-calendar-picker-indicator': {
                    backgroundColor: customColors?.primary || 'primary.main',
                    backgroundImage: 'none',
                    maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center',
                    maskSize: 'contain',
                    WebkitMaskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    WebkitMaskSize: 'contain',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }
                }
              }}
            />
            {/* Presets de intervalo */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: { xs: 1, sm: 0 } }}>
              {periodPresets.map(preset => (
                <Button
                  key={preset.key}
                  variant={activePreset === preset.key ? 'contained' : 'outlined'}
                  color={activePreset === preset.key ? 'primary' : 'inherit'}
                  size="small"
                  onClick={() => {
                    const [start, end] = preset.getRange();
                    setPeriodStart(start.toISOString().split('T')[0]);
                    setPeriodEnd(end.toISOString().split('T')[0]);
                    setActivePreset(preset.key);
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </Box>
            {/* Select de hábitos */}
            <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 180, flexShrink: 0, ml: { xs: 0, sm: 2 } }}>
              <InputLabel id="habits-select-label">Hábitos</InputLabel>
              <Select
                labelId="habits-select-label"
                multiple
                value={selectedHabits}
                onChange={handleHabitDropdown}
                input={<OutlinedInput label="Hábitos" />}
                renderValue={(selected) => {
                  if (selected.length === 1 && selected[0] === 'todos') return 'Todos';
                  return habitOptions.filter(h => selected.includes(h.id)).map(h => h.name).join(', ');
                }}
              >
                <MenuItem value="todos">
                  <Checkbox checked={selectedHabits.length === 1 && selectedHabits[0] === 'todos'} />
                  <span>Todos</span>
                </MenuItem>
                {habitOptions.map(habit => (
                  <MenuItem key={habit.id} value={habit.id}>
                    <Checkbox checked={selectedHabits.length === 1 && selectedHabits[0] === 'todos' ? false : selectedHabits.includes(habit.id)} />
                    <span>{habit.name}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        {/* Cards de Estatísticas Gerais de Hábitos */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<TrendingUpIcon />}
              title="Melhor Mês"
              value={filteredStats.bestMonth ? `${filteredStats.bestMonth.name}/${filteredStats.bestMonth.year} (${filteredStats.bestMonth.rate}%)` : '-'}
              color="success"
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<TrendingDownIcon />}
              title="Pior Mês"
              value={filteredStats.worstMonth ? `${filteredStats.worstMonth.name}/${filteredStats.worstMonth.year} (${filteredStats.worstMonth.rate}%)` : '-'}
              color="error"
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<EmojiEventsIcon />}
              title="Total de Dias Perfeitos"
              value={String(filteredStats.totalPerfectDays || 0)}
              color="info"
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<CheckCircleIcon />}
              title="Total de Hábitos Concluídos"
              value={String(filteredStats.totalCompletions || 0)}
              color="warning"
              customColors={customColors}
            />
          </Grid>
        </Grid>
        {/* Gráfico 2: Realizados */}
        <Box sx={{ width: '100%', mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Realizados vs Programados</Typography>
          <FormControlLabel
            control={<Switch checked={highlightPerfectDays} onChange={e => setHighlightPerfectDays(e.target.checked)} color="success" />}
            label="Destacar dias perfeitos em verde"
            sx={{ mb: 1 }}
          />
          <Box sx={{ width: '100%' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={realizedVsPlannedData} margin={{ top: 10, right: 0, left: -40, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(255,255,255,0.18)' : '#ccc'} />
                <XAxis dataKey="date" tickFormatter={formatXAxisDate} fontSize={12} stroke={isDarkMode ? '#fff' : '#222'} tick={{ fill: isDarkMode ? '#fff' : '#222' }} />
                <YAxis allowDecimals={false} fontSize={12} stroke={isDarkMode ? '#fff' : '#222'} tick={{ fill: isDarkMode ? '#fff' : '#222' }} />
                <Tooltip
                  labelFormatter={formatTooltipDate}
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    const isDark = isDarkMode;
                    return (
                      <Box sx={{
                        bgcolor: isDark ? '#222' : 'white',
                        color: isDark ? 'white' : 'black',
                        p: 1.2,
                        border: '1px solid',
                        borderColor: isDark ? '#444' : '#ccc',
                        borderRadius: 1,
                        minWidth: 80
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: isDark ? 'white' : 'black' }}>{formatTooltipLabelToFullDate(label, payload)}</Typography><br />
                        {payload.map((entry, i) => (
                          <Typography key={i} variant="caption" sx={{ color: isDark ? 'white' : 'black' }}>
                            {entry.name}: {entry.value}
                          </Typography>
                        ))}
                      </Box>
                    );
                  }}
                />
                <Bar dataKey="Realizados"
                  fill={customColors?.primary || '#1976d2'}
                  activeBar={{
                    fill: theme.palette.mode === 'dark'
                      ? (customColors?.primary || '#FFD600')
                      : (customColors?.primary || '#1976d2'),
                    opacity: 0.85,
                    stroke: customColors?.primary || (theme.palette.mode === 'dark' ? '#FFD600' : '#1976d2'),
                    strokeWidth: 2
                  }}
                  shape={highlightPerfectDays ? (props => {
                    const { x, y, width, height, payload } = props;
                    const isPerfect = payload.perfeito;
                    return (
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        fill={isPerfect ? '#43a047' : (customColors?.primary || '#1976d2')}
                      />
                    );
                  }) : undefined}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        {/* CTA: Assine o PRO para estatísticas avançadas */}
        <Box
          sx={{
            display: 'block',
            mt: 2,
            mb: 3,
            p: 2,
            borderRadius: 2,
            textDecoration: 'none',
            background: `linear-gradient(135deg, ${customColors?.primary || '#1976d2'}22, ${customColors?.secondary || '#78B6CB'}22)`,
            border: '1px solid',
            borderColor: customColors?.primary || 'primary.main',
            color: 'text.primary',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 2
            }
          }}
          onClick={() => setOpenPlanDialog(true)}
        >
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
            Para ter acesso a estatísticas avançadas, assine o plano PRO
          </Typography>
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: customColors?.primary || 'primary.main',
              fontWeight: 600,
              textDecoration: 'underline'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setOpenPlanDialog(true);
            }}
          >
            Ver plano PRO →
          </Typography>
        </Box>
        <PlanComparisonDialog
          open={openPlanDialog}
          onClose={() => setOpenPlanDialog(false)}
          customColors={customColors}
          title="Conheça o Plano PRO para estatísticas avançadas"
        />
      </Box>
    );
  }

  // Return principal para o plano Pro
  return (
    <Box sx={{ p: 0, pt: { xs: 0, sm: 2 } }}>
      {/* Filtros de período e seleção de hábitos, com presets de intervalo */}
      <Box sx={{ mb: { xs: 1.5, sm: 5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' }, mb: 2 }}>
          <TextField
            label="Data Inicial"
            type="date"
            size="small"
            value={periodStart}
            onChange={e => setPeriodStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160, flexShrink: 0, mr: 1, mb: { xs: 1, sm: 0 } }}
            InputProps={{
              sx: {
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  backgroundColor: customColors?.primary || 'primary.main',
                  backgroundImage: 'none',
                  maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  maskSize: 'contain',
                  WebkitMaskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  WebkitMaskSize: 'contain',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }
              }
            }}
          />
          <TextField
            label="Data Final"
            type="date"
            size="small"
            value={periodEnd}
            onChange={e => setPeriodEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160, mr: 2, mb: { xs: 1, sm: 0 } }}
            InputProps={{
              sx: {
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  backgroundColor: customColors?.primary || 'primary.main',
                  backgroundImage: 'none',
                  maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  maskSize: 'contain',
                  WebkitMaskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  WebkitMaskSize: 'contain',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }
              }
            }}
          />
          {/* Presets de intervalo */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: { xs: 1, sm: 0 } }}>
            {periodPresets.map(preset => (
              <Button
                key={preset.key}
                variant={activePreset === preset.key ? 'contained' : 'outlined'}
                color={activePreset === preset.key ? 'primary' : 'inherit'}
                size="small"
                onClick={() => {
                  const [start, end] = preset.getRange();
                  setPeriodStart(start.toISOString().split('T')[0]);
                  setPeriodEnd(end.toISOString().split('T')[0]);
                  setActivePreset(preset.key);
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Box>
          {/* Select de hábitos */}
          <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 180, flexShrink: 0, ml: { xs: 0, sm: 2 } }}>
            <InputLabel id="habits-select-label">Hábitos</InputLabel>
            <Select
              labelId="habits-select-label"
              multiple
              value={selectedHabits}
              onChange={handleHabitDropdown}
              input={<OutlinedInput label="Hábitos" />}
              renderValue={(selected) => {
                if (selected.length === 1 && selected[0] === 'todos') return 'Todos';
                return habitOptions.filter(h => selected.includes(h.id)).map(h => h.name).join(', ');
              }}
            >
              <MenuItem value="todos">
                <Checkbox checked={selectedHabits.length === 1 && selectedHabits[0] === 'todos'} />
                <span>Todos</span>
              </MenuItem>
              {habitOptions.map(habit => (
                <MenuItem key={habit.id} value={habit.id}>
                  <Checkbox checked={selectedHabits.length === 1 && selectedHabits[0] === 'todos' ? false : selectedHabits.includes(habit.id)} />
                  <span>{habit.name}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      {/* Cards de Estatísticas Gerais de Hábitos */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={<TrendingUpIcon />}
            title="Melhor Mês"
            value={filteredStats.bestMonth ? `${filteredStats.bestMonth.name}/${filteredStats.bestMonth.year} (${filteredStats.bestMonth.rate}%)` : '-'}
            color="success"
            customColors={customColors}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={<TrendingDownIcon />}
            title="Pior Mês"
            value={filteredStats.worstMonth ? `${filteredStats.worstMonth.name}/${filteredStats.worstMonth.year} (${filteredStats.worstMonth.rate}%)` : '-'}
            color="error"
            customColors={customColors}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={<EmojiEventsIcon />}
            title="Total de Dias Perfeitos"
            value={String(filteredStats.totalPerfectDays || 0)}
            color="info"
            customColors={customColors}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={<CheckCircleIcon />}
            title="Total de Hábitos Concluídos"
            value={String(filteredStats.totalCompletions || 0)}
            color="warning"
            customColors={customColors}
          />
        </Grid>
      </Grid>
      {/* Gráfico 2: Realizados */}
      <Box sx={{ width: '100%', mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Gráfico: Realizados</Typography>
        <FormControlLabel
          control={<Switch checked={highlightPerfectDays} onChange={e => setHighlightPerfectDays(e.target.checked)} color="success" />}
          label="Destacar dias perfeitos em verde"
          sx={{ mb: 1 }}
        />
        <Box sx={{ width: '100%' }}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={realizedVsPlannedData} margin={{ top: 10, right: 0, left: -40, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(255,255,255,0.18)' : '#ccc'} />
              <XAxis dataKey="date" tickFormatter={formatXAxisDate} fontSize={12} stroke={isDarkMode ? '#fff' : '#222'} tick={{ fill: isDarkMode ? '#fff' : '#222' }} />
              <YAxis allowDecimals={false} fontSize={12} stroke={isDarkMode ? '#fff' : '#222'} tick={{ fill: isDarkMode ? '#fff' : '#222' }} />
              <Tooltip
                labelFormatter={formatTooltipDate}
                cursor={false}
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  const isDark = isDarkMode;
                  return (
                    <Box sx={{
                      bgcolor: isDark ? '#222' : 'white',
                      color: isDark ? 'white' : 'black',
                      p: 1.2,
                      border: '1px solid',
                      borderColor: isDark ? '#444' : '#ccc',
                      borderRadius: 1,
                      minWidth: 80
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: isDark ? 'white' : 'black' }}>{formatTooltipLabelToFullDate(label, payload)}</Typography><br />
                      {payload.map((entry, i) => (
                        <Typography key={i} variant="caption" sx={{ color: isDark ? 'white' : 'black' }}>
                          {entry.name}: {entry.value}
                        </Typography>
                      ))}
                    </Box>
                  );
                }}
              />
              <Bar dataKey="Realizados"
                fill={customColors?.primary || '#1976d2'}
                activeBar={{
                  fill: theme.palette.mode === 'dark'
                    ? (customColors?.primary || '#FFD600')
                    : (customColors?.primary || '#1976d2'),
                  opacity: 0.85,
                  stroke: customColors?.primary || (theme.palette.mode === 'dark' ? '#FFD600' : '#1976d2'),
                  strokeWidth: 2
                }}
                shape={highlightPerfectDays ? (props => {
                  const { x, y, width, height, payload } = props;
                  const isPerfect = payload.perfeito;
                  return (
                    <rect
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      fill={isPerfect ? '#43a047' : (customColors?.primary || '#1976d2')}
                    />
                  );
                }) : undefined}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
      {/* Gráfico de Vida - Equilíbrio entre Pilares */}
      <Box sx={{ width: '100%', mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
          Gráfico de Vida (Equilíbrio entre Pilares)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mostra, para o período selecionado, o quão bem você está cumprindo os hábitos de cada pilar.
        </Typography>
        <Box sx={{ width: '100%', p: 2, background: 'rgba(255,255,255,0.02)', borderRadius: 2, boxShadow: 1 }}>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={pillarRadarData}>
              <PolarGrid stroke={isDarkMode ? 'rgba(255,255,255,0.18)' : '#ccc'} />
              <PolarAngleAxis
                dataKey="name"
                tick={{ fill: isDarkMode ? '#fff' : '#222', fontSize: 12 }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                tick={{ fill: isDarkMode ? '#fff' : '#222', fontSize: 10 }}
                tickFormatter={(v) => `${v}%`}
              />
              <Radar
                name="Equilíbrio"
                dataKey="score"
                stroke={customColors?.primary || '#1976d2'}
                fill={customColors?.primary || '#1976d2'}
                fillOpacity={0.35}
              />
              <Tooltip
                cursor={false}
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  const entry = payload[0].payload;
                  const isDark = isDarkMode;
                  return (
                    <Box sx={{
                      bgcolor: isDark ? '#222' : 'white',
                      color: isDark ? 'white' : 'black',
                      p: 1.2,
                      border: '1px solid',
                      borderColor: isDark ? '#444' : '#ccc',
                      borderRadius: 1,
                      minWidth: 140
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: isDark ? 'white' : 'black' }}>
                        {label}
                      </Typography>
                      <br />
                      <Typography variant="caption">
                        Equilíbrio: {entry.score}%
                      </Typography>
                      <br />
                      <Typography variant="caption">
                        Programados: {entry.scheduled}
                      </Typography>
                      <br />
                      <Typography variant="caption">
                        Realizados: {entry.completed}
                      </Typography>
                    </Box>
                  );
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Gráficos avançados do Pro em linha no desktop */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Donut */}
        <Grid item xs={12} md={4}>
          <Box sx={{ width: '100%', p: 2, background: 'rgba(255,255,255,0.02)', borderRadius: 2, boxShadow: 1, mb: { xs: 2, md: 0 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, textAlign: 'center' }}>Proporção de Programados vs Realizados</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={(() => {
                    let programados = 0;
                    let realizados = 0;
                    const dates = getDatesInRange(periodStart, periodEnd);
                    const selected = selectedHabits.includes('todos') ? habitOptions.map(h => h.id) : selectedHabits;
                    dates.forEach(dateStr => {
                      habitOptions.forEach(habit => {
                        if (!selected.includes(habit.id)) return;
                        const jsDate = new Date(dateStr);
                        const dayOfWeek = jsDate.getDay();
                        const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
                        if (!habit.weekdays?.length || habit.weekdays.includes(adjustedDay)) {
                          programados++;
                          if (habit.completedDates?.some(completedDate => completedDate.split('T')[0] === dateStr)) {
                            realizados++;
                          }
                        }
                      });
                    });
                    return [
                      { name: 'Realizados', value: realizados },
                      { name: 'Programados', value: Math.max(programados - realizados, 0) }
                    ];
                  })()}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  fill={customColors?.primary || '#1976d2'}
                >
                  <Cell key="realizados" fill={customColors?.primary || '#43a047'} />
                  <Cell key="programados" fill={customColors?.secondary || '#e0e0e0'} />
                </Pie>
                <Tooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    const isDark = isDarkMode;
                    return (
                      <Box sx={{
                        bgcolor: isDark ? '#222' : 'white',
                        color: isDark ? 'white' : 'black',
                        p: 1.2,
                        border: '1px solid',
                        borderColor: isDark ? '#444' : '#ccc',
                        borderRadius: 1,
                        minWidth: 80
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: isDark ? 'white' : 'black' }}>{label}</Typography><br />
                        {payload.map((entry, i) => (
                          <Typography key={i} variant="caption" sx={{ color: isDark ? 'white' : 'black' }}>
                            {entry.name}: {entry.value}
                          </Typography>
                        ))}
                      </Box>
                    );
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
        {/* Barras: Produtividade por Dia da Semana */}
        <Grid item xs={12} md={4}>
          <Box sx={{ width: '100%', p: 2, background: 'rgba(255,255,255,0.02)', borderRadius: 2, boxShadow: 1, mb: { xs: 2, md: 0 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, textAlign: 'center' }}>Produtividade por Dia da Semana</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={(() => {
                const dias = [
                  { dia: 'Dom', programados: 0, realizados: 0 },
                  { dia: '2ªF', programados: 0, realizados: 0 },
                  { dia: '3ªF', programados: 0, realizados: 0 },
                  { dia: '4ªF', programados: 0, realizados: 0 },
                  { dia: '5ªF', programados: 0, realizados: 0 },
                  { dia: '6ªF', programados: 0, realizados: 0 },
                  { dia: 'Sáb', programados: 0, realizados: 0 }
                ];
                const dates = getDatesInRange(periodStart, periodEnd);
                const selected = selectedHabits.includes('todos') ? habitOptions.map(h => h.id) : selectedHabits;
                dates.forEach(dateStr => {
                  const jsDate = new Date(dateStr);
                  const dayIdx = jsDate.getDay();
                  habitOptions.forEach(habit => {
                    if (!selected.includes(habit.id)) return;
                    // Respeita a data de início do hábito
                    if (habit.startDate) {
                      const parts = habit.startDate.split('-');
                      if (parts.length === 3) {
                        const [y, m, d] = parts.map(Number);
                        if (y && m && d) {
                          const start = new Date(y, m - 1, d);
                          start.setHours(0, 0, 0, 0);
                          if (jsDate < start) return;
                        }
                      }
                    }
                    const adjustedDay = dayIdx === 0 ? 7 : dayIdx;
                    if (!habit.weekdays?.length || habit.weekdays.includes(adjustedDay)) {
                      dias[dayIdx].programados++;
                      if (habit.completedDates?.some(completedDate => completedDate.split('T')[0] === dateStr)) {
                        dias[dayIdx].realizados++;
                      }
                    }
                  });
                });
                return dias;
              })()} margin={{ top: 10, right: 0, left: 0, bottom: 10 }}>
                <XAxis dataKey="dia" fontSize={12} tick={{ fill: isDarkMode ? '#fff' : '#222' }} stroke={isDarkMode ? '#fff' : '#222'} />
                <YAxis allowDecimals={false} fontSize={12} tick={{ fill: isDarkMode ? '#fff' : '#222' }} stroke={isDarkMode ? '#fff' : '#222'} />
                <Tooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    const isDark = isDarkMode;
                    return (
                      <Box sx={{
                        bgcolor: isDark ? '#222' : 'white',
                        color: isDark ? 'white' : 'black',
                        p: 1.2,
                        border: '1px solid',
                        borderColor: isDark ? '#444' : '#ccc',
                        borderRadius: 1,
                        minWidth: 80
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: isDark ? 'white' : 'black' }}>{label}</Typography><br />
                        {payload.map((entry, i) => (
                          <Typography key={i} variant="caption" sx={{ color: isDark ? 'white' : 'black' }}>
                            {entry.name}: {entry.value}
                          </Typography>
                        ))}
                      </Box>
                    );
                  }}
                />
                <Legend />
                <Bar dataKey="programados" name="Programados" fill={customColors?.secondary || '#e0e0e0'} />
                <Bar dataKey="realizados" name="Realizados" fill={customColors?.primary || '#43a047'} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
        {/* Ranking de Hábitos */}
        <Grid item xs={12} md={4}>
          <Box sx={{ width: '100%', p: 2, background: 'rgba(255,255,255,0.02)', borderRadius: 2, boxShadow: 1, mb: { xs: 2, md: 0 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, textAlign: 'center' }}>Ranking de Hábitos Mais Realizados</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                layout="vertical"
                data={(() => {
                const dates = getDatesInRange(periodStart, periodEnd);
                return filteredHabitOptions.map(habit => {
                  let realizados = 0;
                  dates.forEach(dateStr => {
                    const jsDate = new Date(dateStr);
                    // Respeita a data de início do hábito
                    if (habit.startDate) {
                      const parts = habit.startDate.split('-');
                      if (parts.length === 3) {
                        const [y, m, d] = parts.map(Number);
                        if (y && m && d) {
                          const start = new Date(y, m - 1, d);
                          start.setHours(0, 0, 0, 0);
                          if (jsDate < start) return;
                        }
                      }
                    }
                    if (habit.completedDates?.some(completedDate => completedDate.split('T')[0] === dateStr)) {
                      realizados++;
                    }
                  });
                  return { nome: habit.name, realizados };
                }).sort((a, b) => b.realizados - a.realizados);
              })()}
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
              >
                <XAxis type="number" allowDecimals={false} fontSize={12} tick={{ fill: isDarkMode ? '#fff' : '#222' }} stroke={isDarkMode ? '#fff' : '#222'} />
                <YAxis dataKey="nome" type="category" width={120} fontSize={12} tick={{ fill: isDarkMode ? '#fff' : '#222' }} stroke={isDarkMode ? '#fff' : '#222'} />
                <Tooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    const isDark = isDarkMode;
                    return (
                      <Box sx={{
                        bgcolor: isDark ? '#222' : 'white',
                        color: isDark ? 'white' : 'black',
                        p: 1.2,
                        border: '1px solid',
                        borderColor: isDark ? '#444' : '#ccc',
                        borderRadius: 1,
                        minWidth: 80
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: isDark ? 'white' : 'black' }}>{label}</Typography><br />
                        {payload.map((entry, i) => (
                          <Typography key={i} variant="caption" sx={{ color: isDark ? 'white' : 'black' }}>
                            {entry.name}: {entry.value}
                          </Typography>
                        ))}
                      </Box>
                    );
                  }}
                />
                <Bar dataKey="realizados" name="Realizados" fill={customColors?.primary || '#1976d2'} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
      {/* Correlação entre Hábitos */}
      <Box sx={{ width: '100%', mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Correlação entre Hábitos</Typography>
        {filteredHabitOptions.length > 1 ? (
          <Box
            sx={{
              overflowX: 'auto',
              pb: 2,
              display: { xs: 'block', sm: 'flex' },
              justifyContent: { xs: 'flex-start', sm: 'center' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              width: '100%',
              px: { xs: 1, sm: 0 },
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `130px repeat(${filteredHabitOptions.length}, 130px)`,
                minWidth: { xs: Math.max(130 + 130 * filteredHabitOptions.length, 320), sm: 130 + 130 * filteredHabitOptions.length },
                borderCollapse: 'collapse',
                borderSpacing: 0
              }}
            >
              {/* Cabeçalho superior esquerdo vazio */}
              <Box sx={{ width: 130, height: 60 }} />
              {/* Cabeçalho horizontal */}
              {filteredHabitOptions.map(hB => (
                <Box
                  key={hB.id}
                  sx={{
                    width: 130,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 500,
                    fontSize: 15,
                    color: customColors?.primary || 'primary.main',
                    px: 1,
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    wordBreak: 'break-word',
                    lineHeight: 1.1
                  }}
                  title={hB.name}
                >
                  {hB.name}
                </Box>
              ))}
              {/* Linhas da matriz */}
              {filteredHabitOptions.map(hA => [
                // Nome do hábito A (vertical)
                <Box key={hA.id + '-rowname'} sx={{ width: 130, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 2, fontWeight: 500, fontSize: 15, color: customColors?.primary || 'primary.main', whiteSpace: 'normal', overflow: 'visible', textOverflow: 'initial', wordBreak: 'break-word', textAlign: 'right', lineHeight: 1.1 }} title={hA.name}>{hA.name}</Box>,
                // Células da matriz
                ...filteredHabitOptions.map(hB => {
                  let totalA = 0;
                  let totalAB = 0;
                  const dates = getDatesInRange(periodStart, periodEnd);
                  dates.forEach(dateStr => {
                    const aDone = hA.completedDates?.some(cd => cd.split('T')[0] === dateStr);
                    const bDone = hB.completedDates?.some(cd => cd.split('T')[0] === dateStr);
                    if (aDone) {
                      totalA++;
                      if (bDone) totalAB++;
                    }
                  });
                  const corr = totalA > 0 ? totalAB / totalA : null;
                  let bg = '#eee';
                  let text = '#222';
                  if (hA.id === hB.id) {
                    bg = '#bdbdbd';
                    text = '#222';
                  } else if (corr !== null) {
                    bg = interpolateMainColor(corr);
                    text = getContrastingText(bg);
                  }
                  return (
                    <Box
                      key={hA.id + '-' + hB.id}
                      sx={{
                        width: 130,
                        height: 40,
                        bgcolor: bg,
                        color: text,
                        border: '1px solid #e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: hA.id === hB.id ? 700 : 500,
                        fontSize: 15,
                        cursor: hA.id !== hB.id && corr !== null ? 'pointer' : 'default',
                        transition: 'background 0.2s',
                        position: 'relative',
                        '&:hover': {
                          boxShadow: hA.id !== hB.id && corr !== null ? 3 : undefined,
                          zIndex: 2
                        }
                      }}
                      title={hA.id === hB.id ? '100%' : corr === null ? 'Sem dados' : `${Math.round(corr * 100)}% dos dias que "${hA.name}" foi realizado, "${hB.name}" também foi realizado.`}
                    >
                      {hA.id === hB.id ? '—' : corr === null ? '-' : `${Math.round(corr * 100)}%`}
                    </Box>
                  );
                })
              ])}
              {/* Dica de arraste no mobile se overflow */}
              {filteredHabitOptions.length > 2 && (
                <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 1, textAlign: 'center', width: '100%' }}>
                  <Typography variant="caption" color="text.secondary">Arraste para o lado para ver toda a matriz</Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Adicione pelo menos dois hábitos para ver a correlação.</Typography>
        )}
      </Box>
      {/* Dotplot: Dias Perfeitos, Parciais e Não Realizados agrupados por mês */}
      <Box sx={{ width: '100%', mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Dotplot: Dias Perfeitos, Parciais e Não Realizados</Typography>
        {(() => {
          // Agrupa os dados por mês/ano
          const months = {};
          dotplotMonthData.forEach(d => {
            if (!months[d.y]) months[d.y] = [];
            months[d.y].push(d);
          });
          // Ordena os meses cronologicamente
          const orderedMonths = Object.keys(months).sort((a, b) => {
            // a, b: '06/24' (mm/aa)
            const [ma, ya] = a.split('/').map(Number);
            const [mb, yb] = b.split('/').map(Number);
            return ya !== yb ? ya - yb : ma - mb;
          });
          // Descobre o maior número de dias em um mês do período para grid
          const maxDays = Math.max(...Object.values(months).map(arr => arr.length));
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {orderedMonths.map((monthLabel, idx) => {
                const days = months[monthLabel].sort((a, b) => a.x - b.x);
                return (
                  <React.Fragment key={monthLabel}>
                    <Box key={monthLabel} sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography variant="caption" sx={{ minWidth: 70, fontWeight: 500, color: customColors?.primary || 'primary.main' }}>{formatMonthLabel(monthLabel)}</Typography>
                      {/* Dotplot: 7 bolinhas por linha só no mobile, linha única no desktop */}
                      {(() => {
                        const daysSorted = months[monthLabel].sort((a, b) => a.x - b.x);
                        if (window.innerWidth < 600) { // mobile
                          const rows = [];
                          for (let i = 0; i < daysSorted.length; i += 7) {
                            rows.push(daysSorted.slice(i, i + 7));
                          }
                          return (
                            <Box sx={{ width: '100%' }}>
                              {rows.map((row, rowIdx) => (
                                <Box
                                  key={rowIdx}
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(7, 1fr)',
                                    gap: 0.5,
                                    width: '100%',
                                    overflow: 'hidden',
                                    minHeight: 16,
                                    mb: 0.5
                                  }}
                                >
                                  {Array.from({ length: 7 }).map((_, idx) => {
                                    const d = row[idx];
                                    return d ? (
                                      <Box
                                        key={d.date || idx}
                                        sx={{
                                          width: 12,
                                          height: 12,
                                          borderRadius: '50%',
                                          bgcolor: d.color,
                                          border: d.color === '#fbc02d' ? '2px solid #333' : '1px solid #333',
                                          display: 'inline-block',
                                          cursor: 'pointer',
                                          transition: 'transform 0.1s',
                                          justifySelf: 'center',
                                          '&:hover': {
                                            transform: 'scale(1.25)',
                                            boxShadow: 2
                                          }
                                        }}
                                        title={`${String(new Date(d.date).getDate()).padStart(2, '0')}/${String(new Date(d.date).getMonth() + 1).padStart(2, '0')}/${String(new Date(d.date).getFullYear()).slice(2)} - ${d.perfeito ? 'Dia perfeito' : d.parcial ? 'Parcialmente realizado' : 'Nenhum hábito realizado'}`}
                                      />
                                    ) : (
                                      <Box key={idx} sx={{ width: 12, height: 12, display: 'inline-block', visibility: 'hidden' }} />
                                    );
                                  })}
                                </Box>
                              ))}
                            </Box>
                          );
                        } else { // desktop
                          return (
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${maxDays}, 1fr)`,
                                gap: 1,
                                width: '100%',
                                minHeight: 24
                              }}
                            >
                              {Array.from({ length: maxDays }).map((_, idx) => {
                                const d = daysSorted[idx];
                                return d ? (
                                  <Box
                                    key={d.date || idx}
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: '50%',
                                      bgcolor: d.color,
                                      border: d.color === '#fbc02d' ? '2px solid #333' : '1px solid #333',
                                      display: 'inline-block',
                                      cursor: 'pointer',
                                      transition: 'transform 0.1s',
                                      justifySelf: 'center',
                                      '&:hover': {
                                        transform: 'scale(1.25)',
                                        boxShadow: 2
                                      }
                                    }}
                                    title={`${String(new Date(d.date).getDate()).padStart(2, '0')}/${String(new Date(d.date).getMonth() + 1).padStart(2, '0')}/${String(new Date(d.date).getFullYear()).slice(2)} - ${d.perfeito ? 'Dia perfeito' : d.parcial ? 'Parcialmente realizado' : 'Nenhum hábito realizado'}`}
                                  />
                                ) : (
                                  <Box key={idx} sx={{ width: 18, height: 18, display: 'inline-block', visibility: 'hidden' }} />
                                );
                              })}
                            </Box>
                          );
                        }
                      })()}
                    </Box>
                    {/* Divider sutil entre meses, exceto após o último mês */}
                    {idx < orderedMonths.length - 1 && (
                      <Divider sx={{ my: 1, borderColor: 'rgba(0,0,0,0.08)' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          );
        })()}
        <Divider sx={{ my: 2, borderColor: 'rgba(0,0,0,0.08)' }} />
        <Box sx={{ display: 'flex', gap: 5, mt: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#43a047', borderRadius: '50%' }} />
            <Typography variant="caption">Dia Perfeito</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#fbc02d', borderRadius: '50%' }} />
            <Typography variant="caption">Parcialmente realizado</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#e53935', borderRadius: '50%' }} />
            <Typography variant="caption">Nenhum hábito realizado</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Statistics; 